/**
 * Sprint 20 B3: Multi-Language Content Editor
 *
 * Language tabs (EN/DE/FR/RO/ES/IT/NL). Edit content per language.
 * AI-translate between versions. Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { useApiMutation } from "@/hooks/use-api";
import { Languages, Sparkles, Save, Plus, Trash2, FileText } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface ContentVariant {
  language: string;
  subject: string;
  body: string;
  lastEditedAt: string | null;
}

interface ContentDocument {
  id: string;
  name: string;
  type: "email" | "page" | "sms";
  variants: ContentVariant[];
  createdAt: string;
}

interface AiSuggestResponse {
  content?: string;
}

interface SuggestedVariant {
  subject: string;
  body: string;
  rationale: string;
}

function parseSuggestedVariant(content: string): SuggestedVariant | null {
  const start = content.indexOf("{");
  const end = content.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) {
    return null;
  }

  try {
    const parsed = JSON.parse(content.slice(start, end + 1)) as Record<string, unknown>;
    const subject = typeof parsed.subject === "string" ? parsed.subject.trim() : "";
    const body = typeof parsed.body === "string" ? parsed.body.trim() : "";
    const rationale = typeof parsed.rationale === "string" ? parsed.rationale.trim() : "";
    if (!subject || !body || !rationale) {
      return null;
    }
    return { subject: subject.slice(0, 200), body, rationale };
  } catch {
    return null;
  }
}

const LANGUAGES = [
  { code: "en", label: "English", flag: "EN" },
  { code: "de", label: "Deutsch", flag: "DE" },
  { code: "fr", label: "Français", flag: "FR" },
  { code: "ro", label: "Română", flag: "RO" },
  { code: "es", label: "Español", flag: "ES" },
  { code: "it", label: "Italiano", flag: "IT" },
  { code: "nl", label: "Nederlands", flag: "NL" },
] as const;

export default function MultiLanguageContentEditor() {
  const [documents, setDocuments] = useState<ContentDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [activeLang, setActiveLang] = useState("en");
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [suggestedVariant, setSuggestedVariant] = useState<SuggestedVariant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const suggestCopy = useApiMutation<{
    prompt: string;
    featureKey: string;
    messages: Array<{ role: "system" | "user"; content: string }>;
  }, AiSuggestResponse>("/crm/ai/complete");

  const fetchDocuments = useCallback(async () => {
    try {
      setError(null);
      const res = await apiFetch<{ data: ContentDocument[] }>("/content/documents");
      setDocuments(res.data);
      if (res.data.length > 0) setActiveDocId((prev) => prev ?? res.data[0]!.id);
    } catch { setDocuments([]); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const activeDoc = documents.find((d) => d.id === activeDocId);
  const activeVariant = activeDoc?.variants.find((v) => v.language === activeLang);
  const hasVariant = !!activeVariant;

  const updateVariant = (field: "subject" | "body", value: string) => {
    if (!activeDoc) return;
    setDocuments((prev) =>
      prev.map((doc) => {
        if (doc.id !== activeDocId) return doc;
        const variants = doc.variants.map((v) =>
          v.language === activeLang ? { ...v, [field]: value, lastEditedAt: new Date().toISOString() } : v,
        );
        return { ...doc, variants };
      }),
    );
  };

  const addLanguageVariant = () => {
    if (!activeDoc || hasVariant) return;
    setDocuments((prev) =>
      prev.map((doc) => {
        if (doc.id !== activeDocId) return doc;
        return {
          ...doc,
          variants: [...doc.variants, { language: activeLang, subject: "", body: "", lastEditedAt: null }],
        };
      }),
    );
  };

  const removeLanguageVariant = () => {
    if (!activeDoc || activeLang === "en") return;
    const confirmed = window.confirm(`Remove ${activeLang.toUpperCase()} variant? This cannot be undone.`);
    if (!confirmed) return;
    setDocuments((prev) =>
      prev.map((doc) => {
        if (doc.id !== activeDocId) return doc;
        return { ...doc, variants: doc.variants.filter((v) => v.language !== activeLang) };
      }),
    );
    setActiveLang("en");
  };

  const handleSave = async () => {
    if (!activeDoc) return;
    setIsSaving(true);
    try {
      await apiFetch(`/content/documents/${activeDoc.id}`, {
        method: "PUT",
        body: JSON.stringify({ variants: activeDoc.variants }),
      });
      setError(null);
    } catch { setError("Failed to save. Please try again."); }
    finally { setIsSaving(false); }
  };

  const handleTranslate = async (targetLang: string) => {
    if (!activeDoc) return;
    const sourceLang = "en";
    const source = activeDoc.variants.find((v) => v.language === sourceLang);
    if (!source || !source.body) return;

    setIsTranslating(true);
    try {
      const res = await apiFetch<{ data: { subject: string; body: string } }>("/ai/translate", {
        method: "POST",
        body: JSON.stringify({
          sourceLang,
          targetLang,
          subject: source.subject,
          body: source.body,
        }),
      });
      setDocuments((prev) =>
        prev.map((doc) => {
          if (doc.id !== activeDocId) return doc;
          const existing = doc.variants.find((v) => v.language === targetLang);
          if (existing) {
            return {
              ...doc,
              variants: doc.variants.map((v) =>
                v.language === targetLang
                  ? { ...v, subject: res.data.subject, body: res.data.body, lastEditedAt: new Date().toISOString() }
                  : v,
              ),
            };
          }
          return {
            ...doc,
            variants: [...doc.variants, {
              language: targetLang,
              subject: res.data.subject,
              body: res.data.body,
              lastEditedAt: new Date().toISOString(),
            }],
          };
        }),
      );
      setActiveLang(targetLang);
      setError(null);
    } catch { setError("Translation failed. Please try again."); }
    finally { setIsTranslating(false); }
  };

  const handleSuggestCopy = async () => {
    if (!activeDoc || !activeVariant?.body.trim()) {
      setError("Add subject and body content before requesting AI improvements.");
      return;
    }

    const response = await suggestCopy.mutate({
      prompt: `Improve the subject and body for ${activeDoc.name} (${activeLang.toUpperCase()}).`,
      featureKey: "crm.assistant.complete",
      messages: [
        {
          role: "system",
          content: [
            "Return strict JSON with keys subject, body, rationale.",
            "Improve clarity, relevance, and conversion potential without changing the language.",
            "Do not return markdown or commentary outside the JSON object.",
            "This output is AI-generated and must be reviewed by a human before it is applied.",
          ].join(" "),
        },
        {
          role: "user",
          content: JSON.stringify({
            documentName: activeDoc.name,
            type: activeDoc.type,
            language: activeLang,
            subject: activeVariant.subject,
            body: activeVariant.body,
          }),
        },
      ],
    });

    const parsed = parseSuggestedVariant(response?.content ?? "");
    if (!parsed) {
      setSuggestedVariant(null);
      setError("AI could not produce a valid subject/body improvement for this document.");
      return;
    }

    setError(null);
    setSuggestedVariant(parsed);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-16 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Languages className="h-5 w-5 text-[var(--color-primary)]" />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Content Editor</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">Multi-language content with AI translation</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !activeDoc}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Skeleton className="h-4 w-4 bg-white/50" />
              <Skeleton className="h-4 w-14 bg-white/50" />
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save All
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2">
          <p className="text-xs text-[var(--color-error)]">{error}</p>
          <button type="button" onClick={() => setError(null)} className="text-xs text-[var(--color-error)] hover:underline">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-[240px_1fr] gap-6">
        {/* Document list */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase text-[var(--color-muted-foreground)] px-2">Documents</h3>
          {documents.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center">
              <FileText className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)] mb-2" />
              <p className="text-xs text-[var(--color-muted-foreground)]">No content documents yet</p>
            </div>
          ) : (
            documents.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => { setActiveDocId(doc.id); setActiveLang("en"); }}
                className={`stagger-item w-full text-left rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  activeDocId === doc.id
                    ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium"
                    : "text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                }`}
              >
                <p className="truncate">{doc.name}</p>
                <p className="text-[10px] text-[var(--color-muted-foreground)]">
                  {doc.variants.length} language{doc.variants.length !== 1 ? "s" : ""} | {doc.type}
                </p>
              </button>
            ))
          )}
        </div>

        {/* Editor */}
        {activeDoc ? (
          <div className="space-y-4">
            {/* Language tabs */}
            <div className="flex items-center gap-1 rounded-lg bg-[var(--color-surface)] p-1">
              {LANGUAGES.map((lang) => {
                const exists = activeDoc.variants.some((v) => v.language === lang.code);
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => setActiveLang(lang.code)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      activeLang === lang.code
                        ? "bg-[var(--color-background)] text-[var(--color-foreground)] shadow-sm"
                        : exists
                        ? "text-[var(--color-foreground)] hover:bg-[var(--color-background)]/50"
                        : "text-[var(--color-muted-foreground)] opacity-50 hover:opacity-100"
                    }`}
                  >
                    {lang.flag}
                    {exists && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-success)]" />}
                  </button>
                );
              })}
            </div>

            {/* AI Translate bar */}
            <div className="flex items-center justify-between rounded-lg border border-[var(--color-ai,var(--color-primary))]/20 bg-[var(--color-ai,var(--color-primary))]/5 px-4 py-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--color-ai,var(--color-primary))]" />
                <span className="text-xs text-[var(--color-ai,var(--color-primary))]">
                  {isTranslating ? "Translating..." : "AI Translate from English"}
                </span>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={handleSuggestCopy}
                  disabled={suggestCopy.isLoading || !hasVariant || !activeVariant?.body}
                  className="rounded px-2 py-1 text-[10px] font-medium text-[var(--color-ai,var(--color-primary))] hover:bg-[var(--color-ai,var(--color-primary))]/10 disabled:opacity-30"
                >
                  {suggestCopy.isLoading ? "Thinking..." : "AI Suggest"}
                </button>
                {LANGUAGES.filter((l) => l.code !== "en").map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleTranslate(lang.code)}
                    disabled={isTranslating || !activeDoc.variants.some((v) => v.language === "en" && v.body)}
                    className="rounded px-2 py-1 text-[10px] font-medium text-[var(--color-ai,var(--color-primary))] hover:bg-[var(--color-ai,var(--color-primary))]/10 disabled:opacity-30"
                  >
                    → {lang.flag}
                  </button>
                ))}
              </div>
            </div>

            {suggestedVariant && (
              <div className="rounded-lg border border-[var(--color-ai,var(--color-primary))]/20 bg-[var(--color-ai,var(--color-primary))]/5 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-ai,var(--color-primary))]">AI Suggestion</p>
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{suggestedVariant.rationale}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSuggestedVariant(null)}
                      className="rounded border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                    >
                      Dismiss
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        updateVariant("subject", suggestedVariant.subject);
                        updateVariant("body", suggestedVariant.body);
                        setSuggestedVariant(null);
                      }}
                      className="rounded bg-[var(--color-ai,var(--color-primary))] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                    >
                      Apply AI Suggestion
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">Suggested subject</p>
                    <p className="mt-1 text-sm text-[var(--color-foreground)]">{suggestedVariant.subject}</p>
                  </div>
                  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">Suggested body</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--color-foreground)]">{suggestedVariant.body}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Content fields */}
            {hasVariant ? (
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--color-muted-foreground)]">
                    Subject / Title ({activeLang.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    value={activeVariant?.subject ?? ""}
                    onChange={(e) => updateVariant("subject", e.target.value)}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-2.5 text-sm text-[var(--color-foreground)]"
                    placeholder="Enter subject..."
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--color-muted-foreground)]">
                    Body ({activeLang.toUpperCase()})
                  </label>
                  <textarea
                    value={activeVariant?.body ?? ""}
                    onChange={(e) => updateVariant("body", e.target.value)}
                    rows={14}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)] font-mono leading-relaxed resize-none"
                    placeholder="Enter content..."
                  />
                </div>
                {activeVariant?.lastEditedAt && (
                  <p className="text-[10px] text-[var(--color-muted-foreground)]">
                    Last edited: {new Date(activeVariant.lastEditedAt).toLocaleString()}
                  </p>
                )}
                {activeLang !== "en" && (
                  <button
                    type="button"
                    onClick={removeLanguageVariant}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-error)]"
                  >
                    <Trash2 className="h-3 w-3" /> Remove {activeLang.toUpperCase()} variant
                  </button>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
                <Languages className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
                <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
                  No {LANGUAGES.find((l) => l.code === activeLang)?.label} version yet
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    type="button"
                    onClick={addLanguageVariant}
                    className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                  >
                    <Plus className="h-4 w-4" /> Create blank
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTranslate(activeLang)}
                    disabled={isTranslating || !activeDoc.variants.some((v) => v.language === "en" && v.body)}
                    className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-ai,var(--color-primary))] px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" /> AI Translate from EN
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-16">
            <p className="text-sm text-[var(--color-muted-foreground)]">Select a document to edit</p>
          </div>
        )}
      </div>
    </div>
  );
}
